import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import Navbar from './components/layout/Navbar';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import Dashboard from './components/dashboard/Dashboard';
import LandingPage from './components/public/LandingPage';
import AboutPage from './components/public/AboutPage';
import PricingPage from './components/public/PricingPage';
import ContactPage from './components/public/ContactPage';
import TermsPage from './components/public/TermsPage';
import PrivacyPage from './components/public/PrivacyPage';
import SubjectsList from './components/content/SubjectsList';
import PastPapers from './components/content/PastPapers';
import VideoLessons from './components/content/VideoLessons';
import SubjectDetail from './components/content/SubjectDetail';
import ProfilePage from './components/profile/ProfilePage';
import PaymentInstructions from './components/payment/PaymentInstructions';
import PaymentSubmission from './components/payment/PaymentSubmission';
import PaymentStatus from './components/payment/PaymentStatus';
import PaymentApproval from './components/admin/PaymentApproval';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import UserManagement from './components/admin/UserManagement';
import ContentManagement from './components/admin/ContentManagement';
import VideoManagement from './components/admin/VideoManagement';
import AffiliateManagement from './components/admin/AffiliateManagement';
import AffiliateDashboard from './components/affiliate/AffiliateDashboard';
import ExamManagement from './components/admin/ExamManagement';
import ExamList from './components/exam/ExamList';
import ExamTaking from './components/exam/ExamTaking';
import ExamResults from './components/exam/ExamResults';
import ExamMode from './components/exam/ExamMode';
import Footer from './components/layout/Footer';
import AIAssistantToggle from './components/ai/AIAssistantToggle';
import { useScrollToTop } from './utils/scrollUtils';
import './App.css';

function AppContent() {
  useScrollToTop(); // Auto-scroll to top on route changes

  return (
    <div className="App">
      <Navbar />
      <main>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/subjects" 
                  element={
                    <ProtectedRoute requireActiveSubscription>
                      <SubjectsList />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/subjects/:subjectId" 
                  element={
                    <ProtectedRoute requireActiveSubscription>
                      <SubjectDetail />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/papers" 
                  element={
                    <ProtectedRoute requireActiveSubscription>
                      <PastPapers />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/videos" 
                  element={
                    <ProtectedRoute requireActiveSubscription>
                      <VideoLessons />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/exams" 
                  element={
                    <ProtectedRoute requireActiveSubscription>
                      <ExamList />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/exam/take/:examId" 
                  element={
                    <ProtectedRoute requireActiveSubscription>
                      <ExamMode>
                        <ExamTaking />
                      </ExamMode>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/exam/results/:resultId" 
                  element={
                    <ProtectedRoute>
                      <ExamResults />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/affiliate" 
                  element={
                    <ProtectedRoute>
                      <AffiliateDashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/payment/instructions" 
                  element={
                    <ProtectedRoute>
                      <PaymentInstructions />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/payment/submit" 
                  element={
                    <ProtectedRoute>
                      <PaymentSubmission />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/payment/status" 
                  element={
                    <ProtectedRoute>
                      <PaymentStatus />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute adminOnly>
                      <AdminLayout>
                        <AdminDashboard />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/users" 
                  element={
                    <ProtectedRoute adminOnly>
                      <AdminLayout>
                        <UserManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/affiliates" 
                  element={
                    <ProtectedRoute adminOnly>
                      <AdminLayout>
                        <AffiliateManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/content" 
                  element={
                    <ProtectedRoute adminOnly>
                      <AdminLayout>
                        <ContentManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/content/papers" 
                  element={
                    <ProtectedRoute adminOnly>
                      <AdminLayout>
                        <ContentManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/content/videos" 
                  element={
                    <ProtectedRoute adminOnly>
                      <AdminLayout>
                        <VideoManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/content/exams" 
                  element={
                    <ProtectedRoute adminOnly>
                      <AdminLayout>
                        <ExamManagement />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/payments" 
                  element={
                    <ProtectedRoute adminOnly>
                      <AdminLayout>
                        <PaymentApproval />
                      </AdminLayout>
                    </ProtectedRoute>
                  } 
                />
              </Routes>
            </main>
            <Footer />
            <AIAssistantToggle />
          </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
